import { Component, Input, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { OnDestroy } from '@angular/core';
import { AppState } from 'src/app/store/app.states';
import { ComponentBase } from 'src/app/views/component-base';
import { commonState_IsSending, commonState_LatestJourney } from 'src/app/store/common/common.selectors';
import { GoToStepAction } from '../../actions';
import { AppButtonComponent, Helper, NumberEntryFieldComponent } from '@ifaa-components/ui-components';
import { AllocateAllToOptionAction, InvestmentOptionsListRequestAction, ResetDataSavedAction, SaveInvestmentOptionsRequestAction, SetUpdatedOptionsAction, UpdateManualSplitSelectedAction } from './actions';
import { investments_DataSaved, investments_Form, investments_FormInvestmentOptions, investments_InvestmentOptions, investments_IsManualSplit, investments_SelectedOptions, investments_TotalPercent } from './selectors';
import { FormGroupState, MarkAsTouchedAction, NgrxFormsModule, SetErrorsAction } from 'ngrx-forms';
import { AsyncPipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InvestmentCardComponent } from './investment-card/investment-card.component';
import { InvestmentItemModel } from 'src/app/model/investment-item.model';
import { distinctUntilChanged } from 'rxjs';
import { InvestmentTooltipComponent } from './investment-tooltip/investment-tooltip.component';
import { InvestmentsModel } from './state';
import { AppButtonDirective } from 'src/app/directives/appbutton.directive';

@Component({
    selector: 'app-nmj-investments',
    templateUrl: './nmj-investments.component.html',
    styleUrls: ['./nmj-investments.component.scss'],
    imports: [
        AsyncPipe,
        ReactiveFormsModule,
        FormsModule,
        NgrxFormsModule,
        AppButtonComponent,
        NgTemplateOutlet,
        NgIf,
        NgFor,
        InvestmentCardComponent,
        InvestmentTooltipComponent,
        NumberEntryFieldComponent,
        AppButtonDirective
    ],
})
export class NmjInvestmentsComponent extends ComponentBase implements OnInit, OnDestroy {
    
    @Input() set step(value: number) {
        this.stepIndex = value;
    }

    isLoading$ = this.store.pipe(select(commonState_IsSending));
    journeySteps$ = this.store.pipe(select(commonState_LatestJourney));
    form$ = this.store.pipe(select(investments_Form));
    formInvestmentOptions$ = this.store.pipe(select(investments_FormInvestmentOptions));
    dataSaved$ = this.store.pipe(select(investments_DataSaved));
    isManualSplit$ = this.store.pipe(select(investments_IsManualSplit));
    investmentOptions$ = this.store.pipe(select(investments_InvestmentOptions));
    selectedOptions$ = this.store.pipe(select(investments_SelectedOptions));
    totalPercent$ = this.store.pipe(select(investments_TotalPercent));

    helper: Helper = new Helper();
    stepIndex: number = 0;
    nextStep: number = 0;
    previousStep: number = 0;
    localInvestmentOptions: InvestmentItemModel[] = null;
    totalPercent: number = 0;

    investmentIndex: number = 0;

    investmentSplitTooltip: string = 'As of 24 May 2025. This information is subject to change. The asset allocation reflects a strategic mix, but the actual allocation may vary due to market movements, contributions, withdrawals, or changes in investment types.';

    constructor(public store: Store<AppState>) {
        super();
    }

    async ngOnInit() {
        super.ngOnInitBase();

        this.sub = this.sub = this.selectedOptions$.pipe(distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y))).subscribe(async (options) => {
            if (options?.options) {

                options.options?.forEach((option) => {
                    this.store.dispatch(
                        new SetErrorsAction(option.controlId, {})
                    );

                    if (option.percent == 0) {
                        return;
                    }

                    if (option.percent < 5) {
                        var errors = {};
                        errors['customError'] =
                            'Value must be greater than or equal to 5.';
                        this.store.dispatch(
                            new SetErrorsAction(option.controlId, errors)
                        );
                    }
                });

                var totalPercent = options.options?.reduce((a, b) => a + (isNaN(b.percent) ? 0 : Math.abs(b.percent)), 0)

                if (totalPercent !== 100) {
                    var errors = {};
                    errors['customError'] =
                        'Total must equal 100%.';
                    this.store.dispatch(new SetErrorsAction(options.parentId, errors))
                }
                else {
                    this.store.dispatch(new SetErrorsAction(options.parentId, {}))
                }
            }
        });


        this.sub = this.totalPercent$.subscribe(x => {
            this.totalPercent = x ?? 0;
        })

        // get investment options
        this.sub = this.investmentOptions$.subscribe(x => {
            if (!x) {
                this.store.dispatch(InvestmentOptionsListRequestAction());
            }
            else {
                // if first time loading, add split option choice
                if (!x.find(option => option.investmentOptionId == -1)) {
                    this.localInvestmentOptions = this.helper.clone(x);

                    this.localInvestmentOptions.push({
                        investmentOptionId: -1,
                        optionName: 'Split my investment option',
                        optionDescription: 'Choose how you split your super between multiple investment options.'
                    } as InvestmentItemModel)

                    this.store.dispatch(SetUpdatedOptionsAction({ investmentOptions: this.localInvestmentOptions }))
                }
            }
        })

        this.sub = this.journeySteps$.subscribe(async x => {
            if (x) {
                this.nextStep = (await this.helper.getValue(this.journeySteps$)).journeySteps.filter(x => x.journeyStep > this.stepIndex)[0]?.journeyStep ?? -1;
                this.previousStep = (await this.helper.getValue(this.journeySteps$)).journeySteps.filter(x => x.journeyStep < this.stepIndex).sort((a, b) => b.journeyStep - a.journeyStep)[0].journeyStep;
            }
        })

        this.sub = this.dataSaved$.subscribe(async x => {
            if (x) {
                // go next step
                this.store.dispatch(GoToStepAction({ index: this.nextStep }));

                // reset data saved prop
                this.store.dispatch(ResetDataSavedAction());
            }
        })
    }

    ngOnDestroy(): void {
        super.ngOnDestroyBase();
    }

    async onMoveBackClick() {
        this.store.dispatch(GoToStepAction({ index: this.previousStep }))
    }
    async onMoveNextClick(
        form: FormGroupState<any>,
    ) {
        if (form.isInvalid) {
            this.store.dispatch(new MarkAsTouchedAction(form.id));
            return;
        }

        var data = (await this.helper.getValue(this.form$)).value.investments.filter(x => x.investmentOptionId !== -1 && x.percentage !== 0);

        var payload = {
            investments: data
        } as InvestmentsModel

        this.store.dispatch(
            SaveInvestmentOptionsRequestAction({
                step: this.nextStep,
                field: 'page-change',
                data: payload
            })
        );
    }

    selectInvestmentOption(investmentOptionId: number) {
        if (investmentOptionId === -1) {
            this.store.dispatch(UpdateManualSplitSelectedAction({ isManualSplit: true }))
        }
        else {
            this.store.dispatch(AllocateAllToOptionAction({ investmentOptionId }))
        }
    }

    getSplitInfo(investmentOption: InvestmentItemModel) {
        var splitGroupings = [];

        investmentOption.investmentDetailGroups.forEach(group => {
            splitGroupings.push(`${group.investmentGroupPercent}% ${group.investmentGroupName}`);
        })

        return splitGroupings.join(' : ')
    }

    trackByFn(index, item) {
        return index;
    }

    getAddMoreText() {
        if (this.totalPercent >= 0 && this.totalPercent < 100) {
            return `Add ${100 - this.totalPercent}% more`;
        }
        if (this.totalPercent > 100) {
            return `Remove ${this.totalPercent - 100}%`;
        }
    }

    cancelInvestmentSplit() {
        this.store.dispatch(UpdateManualSplitSelectedAction({ isManualSplit: false }))
    }
}
